<script setup lang="ts">
import {computed, ref, watch} from "vue";
import {useRoute, useRouter} from "vue-router";
import BirthdayVoucherReminder from "@/birthday-gift/components/BirthdayVoucherReminder.vue";
import GiftScanDialog from "@/birthday-gift/components/scan/GiftScanDialog.vue";
import useCustomerBirthdayVoucher from "@/birthday-gift/composables/useCustomerBirthdayVoucher";
import {BIRTHDAY_GIFT_SCAN_QUERY_KEY} from "@/birthday-gift/birthdayGiftScanShortcut";

/**
 * The birthday gift reminder of a patient file, with the counter scan.
 *
 * Renders nothing when the patient has no gift left to collect.
 */
const props = defineProps<{
    customerId: number;
}>();

const route = useRoute();
const router = useRouter();

const customerId = computed(() => props.customerId);

const {data: voucher, mutate} = useCustomerBirthdayVoucher(customerId);

const scanOpen = ref(false);
const redeemed = ref(false);

/**
 * The detection pop-up opens the scan by sending the main window here with
 * this key. The dialog shows as soon as the voucher is loaded; the key is
 * dropped once it closes so a reload stays quiet.
 */
watch(() => route.query[BIRTHDAY_GIFT_SCAN_QUERY_KEY], (flag) => {
    if (flag) {
        scanOpen.value = true;
    }
}, {immediate: true});

watch(scanOpen, (open) => {
    if (open) {
        return;
    }

    // Refreshing right away would drop the voucher, unmount the banner and
    // the dialog it hosts mid-confirmation: wait until the pharmacist closes it.
    if (redeemed.value) {
        redeemed.value = false;
        mutate();
    }

    if (BIRTHDAY_GIFT_SCAN_QUERY_KEY in route.query) {
        const query = {...route.query};
        delete query[BIRTHDAY_GIFT_SCAN_QUERY_KEY];

        router.replace({query});
    }
});
</script>

<template>
  <div v-if="voucher" class="px-4 py-1">
    <BirthdayVoucherReminder
      :voucher="voucher"
      @scan="scanOpen = true"
    />

    <GiftScanDialog
      v-model:open="scanOpen"
      @redeemed="redeemed = true"
    />
  </div>
</template>
